import React, { useState, useMemo, useEffect } from 'react';
import { MessageSquare, Settings, Menu, X, Star, Play, LogOut, User, ChevronRight, ChevronDown, Activity } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { clsx } from 'clsx';
import { useMCPServers } from '@/hooks/useMCPServers';
import { TabsBar } from './TabsBar';
import { ToolInvocationModal } from './ToolInvocationModal';
import { MCPTool } from '@/types';
import { useTranslation } from '@/i18n';
import { apiClient } from '@/utils/apiClient';

interface LayoutProps {
  children: React.ReactNode;
  currentView: 'chat' | 'settings' | 'analytics';
  onViewChange: (view: 'chat' | 'settings' | 'analytics') => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, currentView, onViewChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(true);
  const [buildVersion, setBuildVersion] = useState<string | null>(null);

  useEffect(() => {
    apiClient.getVersion()
      .then((v) => {
        const parts: string[] = [];
        if (v.build_date) parts.push(v.build_date);
        else if (v.git_hash) parts.push(v.git_hash);
        if (!parts.length) parts.push(`v${v.version}`);
        setBuildVersion(parts.join(' '));
      })
      .catch(() => {});
  }, []);
  const [selectedTool, setSelectedTool] = useState<{ tool: MCPTool; serverName: string; serverId: string } | null>(null);
  const { user, logout } = useAuthStore();
  const { refreshServerStatus } = useMCPServers();
  const { t } = useTranslation();

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const toggleTools = () => {
    setToolsOpen(!toolsOpen);
    refreshServerStatus();
  };

  const handleToolSelect = (tool: MCPTool, serverName: string, serverId: string) => {
    setSelectedTool({ tool, serverName, serverId });
  };

  return (
    <div className="flex h-[100vh] overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 lg:w-[320px] bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t.chat.title}
            </h1>
            {buildVersion && (
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                {buildVersion}
              </span>
            )}
          </div>
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col h-[calc(100vh-64px)]">
          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2">            
            <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
            <button
              onClick={() => onViewChange('chat')}
              className={clsx(
                'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                currentView === 'chat'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              )}
            >
              <MessageSquare className="mr-3 h-5 w-5" />
              {t.nav.chat}
            </button>
            <button
              onClick={() => onViewChange('settings')}
              className={clsx(
                'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                currentView === 'settings'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              )}
            >
              <Settings className="mr-3 h-5 w-5" />
              {t.nav.settings}
            </button>
            <button
              onClick={() => onViewChange('analytics')}
              className={clsx(
                'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                currentView === 'analytics'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              )}
            >
              <Activity className="mr-3 h-5 w-5" />
              {(t.nav as any).analytics ?? 'Analytics'}
            </button>

            {/* Remove Chat Sessions list; use TabsBar instead. Keep Tools pane below. */}

            {/* Collapsible Tools Pane */}
            {currentView === 'chat' && (
              <div className="mt-6 flex flex-col">{/* tools block */}
                <button
                  onClick={toggleTools}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                >
                  <span>{t.tools.availableTools}</span>
                  <span className="text-gray-400">{toolsOpen ? '−' : '+'}</span>
                </button>
                {toolsOpen && (
                  <div className="mt-2 overflow-y-auto chat-scroll max-h-[calc(100vh-14rem)]">
                    {/* Simple inline list sourced from store to avoid new API shape; shows names and counts */}
                    <ToolsList onToolSelect={handleToolSelect} />
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar with file-like tabs when in chat view */}
        <header className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center h-16 px-2">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              {currentView === 'chat' ? (
                <TabsBar />
              ) : (
                <div className="px-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    {currentView === 'analytics'
                      ? ((t as any).analytics?.title ?? 'Analytics')
                      : t.settings.title}
                  </h2>
                </div>
              )}
            </div>

            {/* User menu */}
            {user && (
              <div className="flex items-center space-x-2 px-3 flex-shrink-0">
                <div className="flex items-center space-x-1.5 text-sm text-gray-600 dark:text-gray-300">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.username}</span>
                </div>
                <button
                  onClick={logout}
                  className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={t.auth.logout}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 min-h-0 overflow-hidden">
          {children}
        </main>
      </div>

      {/* Tool Invocation Modal - Rendered at root level to overlay entire viewport */}
      {selectedTool && (
        <ToolInvocationModal
          isOpen={true}
          onClose={() => setSelectedTool(null)}
          tool={selectedTool.tool}
          serverName={selectedTool.serverName}
          serverId={selectedTool.serverId}
        />
      )}
    </div>
  );
};

// Extract semantic group from tool name: "v2_entities_getAllEntities" → "v2.entities"
function extractGroup(toolName: string): string {
  // Tool names follow pattern: {version}_{group}_{operationId}
  // e.g., v2_entities_getAllEntities, config_alerting_list, v1_topology_getAllApplications
  const parts = toolName.split('_');
  if (parts.length >= 3) {
    return `${parts[0]}.${parts[1]}`;
  }
  if (parts.length === 2) {
    return parts[0];
  }
  return 'other';
}

// Friendly label for a group: "v2.entities" → "Entities (v2)"
function groupLabel(group: string): string {
  const [version, name] = group.split('.');
  if (!name) return group;
  const label = name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ');
  return `${label} (${version})`;
}

interface ToolGroup {
  name: string;
  label: string;
  tools: { tool: MCPTool; serverName: string; serverId: string }[];
}

// Local component to render tools for each enabled MCP server from store
interface ToolsListProps {
  onToolSelect: (tool: MCPTool, serverName: string, serverId: string) => void;
}

const ToolsList: React.FC<ToolsListProps> = ({ onToolSelect }) => {
  const { mcpServers, toggleFavoriteTool } = useAppStore();
  const [query, setQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const enabledServers = mcpServers.filter(s => s.enabled);
  const { t, language } = useTranslation();

  // Build grouped tools across all servers
  const groups = useMemo(() => {
    const groupMap = new Map<string, ToolGroup>();
    const q = query.toLowerCase().trim();

    for (const server of enabledServers) {
      for (const tool of server.tools || []) {
        // Filter by search query
        if (q && !tool.name.toLowerCase().includes(q) && !(tool.description || '').toLowerCase().includes(q)) {
          continue;
        }
        const gName = extractGroup(tool.name);
        if (!groupMap.has(gName)) {
          groupMap.set(gName, { name: gName, label: groupLabel(gName), tools: [] });
        }
        groupMap.get(gName)!.tools.push({ tool, serverName: server.name, serverId: server.id });
      }
    }

    // Sort groups alphabetically by label
    return Array.from(groupMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [enabledServers, query]);

  const totalTools = groups.reduce((sum, g) => sum + g.tools.length, 0);

  // Detect if tools are still loading: enabled servers exist but none have tools
  // and none have reached a terminal status (connected/error).
  const isToolsLoading = enabledServers.length > 0
    && totalTools === 0
    && enabledServers.every(s => !s.tools?.length && s.status === 'disconnected');

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // When searching, auto-expand all groups that have matches
  const isExpanded = (groupName: string) => {
    if (query.trim()) return true;
    return expandedGroups.has(groupName);
  };

  return (
    <div className="space-y-1 px-3">
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 pb-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.common.search + '...'}
          className="w-full px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        />
        <div className="text-[10px] text-gray-400 mt-1 px-1">
          {isToolsLoading
            ? (language === 'ru' ? 'Загрузка инструментов…' : 'Loading tools…')
            : `${totalTools} ${t.nav.tools.toLowerCase()} / ${groups.length} ${language === 'ru' ? 'групп' : 'groups'}`
          }
        </div>
      </div>

      {isToolsLoading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 px-1 py-2">
          <div className="h-3 w-3 border-2 border-gray-300 dark:border-gray-500 border-t-blue-500 rounded-full animate-spin" />
          {language === 'ru' ? 'Подключение к серверу…' : 'Connecting to server…'}
        </div>
      ) : groups.length === 0 ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 px-1">
          {query.trim() ? (language === 'ru' ? 'Ничего не найдено' : 'No matches') : t.tools.noTools}
        </div>
      ) : (
        groups.map(group => (
          <div key={group.name} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => toggleGroup(group.name)}
              className="w-full px-2.5 py-1.5 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
              <div className="flex items-center space-x-1.5 min-w-0">
                {isExpanded(group.name) ? (
                  <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                )}
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">{group.label}</span>
              </div>
              <span className="text-[10px] text-gray-400 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2">
                {group.tools.length}
              </span>
            </button>

            {isExpanded(group.name) && (
              <ul className="px-2 py-1 space-y-0.5">
                {group.tools.map(({ tool, serverName, serverId }, idx) => (
                  <li key={`${serverId}-${idx}`} className="group flex items-center justify-between">
                    <button
                      onClick={() => onToolSelect(tool, serverName, serverId)}
                      className="flex-1 flex items-center min-w-0 text-left py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                      title={tool.description || tool.name}
                    >
                      <Play className="h-3 w-3 mr-1.5 text-blue-500 dark:text-blue-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span className="text-xs text-gray-600 dark:text-gray-300 truncate">
                        {tool.name.replace(/^[^_]+_[^_]+_/, '')}
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavoriteTool(serverId, tool.name);
                      }}
                      className={clsx(
                        'p-1 rounded transition-colors flex-shrink-0',
                        tool.favorite
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                      )}
                      title={tool.favorite ? t.tools.removeFromFavorites : t.tools.addToFavorites}
                    >
                      <Star className={clsx('h-3 w-3', tool.favorite && 'fill-current')} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
};